/**
 * wp-content.ts — innhold for /wordpress (norsk + engelsk)
 *
 * ⚠ PLASSHOLDERE: pakker, priser, antall nettsteder og besøk, lagring, PHP-versjoner,
 * staging, backup-intervall, caching, WP-CLI/SSH, flytting, WooCommerce-støtte, FAQ og
 * kontaktadresse er foreløpige og MÅ erstattes med det som faktisk leveres før siden
 * publiseres. Teknisk spesifikasjon og flytting avgjør valget for kundene, så alt må
 * leses gjennom av eier. All redigering gjøres her — komponentene har ingen egne tekster.
 *
 * VAREMERKE: «WordPress» er et varemerke tilhørende WordPress Foundation, og navnet
 * inWordPress inneholder det. WordPress Foundation er restriktive med bruk av varemerket i
 * produkt- og firmanavn og domener (en vanlig beskrivende bruk som «WordPress-hosting»
 * er normalt uproblematisk). Bevisst valg: produktet heter inWordPress. Footer og FAQ har
 * en avklaring om at inSuite ikke er tilknyttet. Få navnebruken bekreftet juridisk (og
 * evt. avklart med WordPress Foundation) før publisering; «inWP» er et alternativ.
 *
 * Offentlig side: statisk innhold i frontend er tillatt. Når prisene skal styres fra
 * systemet, erstattes denne filen av et API-kall (WpContent i src/types/wp.ts er
 * kontrakten).
 */

import type { Locale } from '@/types/landing';
import type { WpContent } from '@/types/wp';

/** TODO: bekreft riktig kontaktadresse (admin@ntune.no er den eneste i koden i dag). */
const CONTACT = 'mailto:admin@ntune.no?subject=inWordPress';

const no: WpContent = {
  seoTitle: 'inWordPress — WordPress-hosting fra inSuite',
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }],
    current: 'inWordPress',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'funksjoner', label: 'Funksjoner' },
      { id: 'pakker', label: 'Pakker' },
      { id: 'sammenlign', label: 'Sammenlign' },
      { id: 'kom-i-gang', label: 'Kom i gang' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se pakkene', href: '#pakker' },
  },
  hero: {
    heading: 'inWordPress',
    subheading:
      'WordPress-hosting som er satt opp, oppdatert og sikkerhetskopiert for deg, i tre størrelser du kan bytte mellom når nettstedene dine vokser.',
    primaryCta: { label: 'Se pakker og priser', href: '#pakker' },
    secondaryCta: { label: 'Dette får du', href: '#funksjoner' },
    illustrationLabel: 'Illustrasjon av et nettsted, et skjold og et speedometer',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'WordPress som bare virker.',
    body: [
      'inWordPress er hosting som er satt opp for WordPress. Vi tar oss av oppdateringer, sikkerhetskopier og sikkerhet, slik at du kan bruke tiden på innholdet i stedet for driften.',
      'Trenger du bare plass til nettsider, e-post og databaser uten WordPress? Da passer inHost bedre.',
    ],
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Det som skal til for å drive WordPress trygt og raskt.',
    panelLabel: 'Illustrasjon av et live-nettsted og en staging-kopi',
    items: [
      { title: 'Installasjon med ett klikk', body: 'Start med et ferdig oppsatt WordPress-nettsted, eller flytt inn det du har fra før.' },
      { title: 'Automatiske oppdateringer', body: 'WordPress, tillegg og PHP holdes oppdatert, så du slipper å følge med selv.' },
      { title: 'Daglig backup', body: 'Sikkerhetskopi av nettstedet hver dag, og gjenoppretting når du trenger det.' },
      { title: 'Staging-miljø', body: 'Test endringer på en kopi av nettstedet før du publiserer dem.' },
      { title: 'Rask ytelse', body: 'Hurtigbuffer og rask lagring, slik at sidene dine laster raskt.' },
      { title: 'Sikkerhet', body: 'Brannmur og SSL-sertifikat følger med alle pakker.' },
      { title: 'WP-CLI og SSH', body: 'Styr nettstedet fra kommandolinjen når du trenger full kontroll.' },
    ],
  },
  promo: {
    title: 'Bruker du ikke WordPress?',
    body: 'Trenger du bare plass til nettsider, e-post og databaser, er inHost enklere.',
    cta: { label: 'Se inHost', href: '/webhotell' },
  },
  plans: {
    eyebrow: 'Pakker',
    heading: 'Pakker og priser',
    intro: 'Velg pakken som passer, og gå opp en størrelse når nettstedene dine vokser.',
    cardsHeading: 'Velg pakke',
    fromLabel: 'Fra',
    footnote: '*Prisene er veiledende, per måned og eksklusiv mva.',
    items: [
      {
        id: 'start',
        name: 'Start',
        summary: 'For ett nettsted.',
        price: '129',
        priceNote: 'kr / mnd',
        features: ['1 nettsted', '25 000 besøk per måned', '10 GB SSD-lagring', 'Daglig backup', 'SSL-sertifikat'],
        cta: { label: 'Bestill Start', href: CONTACT },
      },
      {
        id: 'vekst',
        name: 'Vekst',
        summary: 'For virksomheter med flere nettsteder.',
        price: '299',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i Start, pluss',
        features: ['5 nettsteder', '100 000 besøk per måned', '50 GB SSD-lagring', 'Staging-miljø', 'WP-CLI og SSH'],
        cta: { label: 'Bestill Vekst', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Anbefalt',
      },
      {
        id: 'byra',
        name: 'Byrå',
        summary: 'For byråer og krevende nettsteder.',
        price: '699',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i Vekst, pluss',
        features: ['20 nettsteder', '500 000 besøk per måned', '200 GB SSD-lagring', 'Prioritert support'],
        cta: { label: 'Bestill Byrå', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Sammenlign',
    heading: 'Sammenlign pakkene',
    intro: 'Se hva hver pakke inneholder.',
    featureLabel: 'Funksjon',
    yes: 'Inkludert',
    no: 'Ikke inkludert',
    rows: [
      { label: 'Kapasitet', values: [], group: true },
      { label: 'Nettsteder', values: ['1', '5', '20'] },
      { label: 'Besøk per måned', values: ['25 000', '100 000', '500 000'] },
      { label: 'SSD-lagring', values: ['10 GB', '50 GB', '200 GB'] },
      { label: 'Funksjoner og support', values: [], group: true },
      { label: 'Daglig backup', values: [true, true, true] },
      { label: 'SSL-sertifikat', values: [true, true, true] },
      { label: 'Staging-miljø', values: [false, true, true] },
      { label: 'WP-CLI og SSH', values: [false, true, true] },
      { label: 'Prioritert support', values: [false, false, true] },
    ],
  },
  steps: {
    eyebrow: 'Kom i gang',
    heading: 'Fra bestilling til publisert nettsted',
    intro: 'Fire steg, og du trenger ikke sette opp noe selv.',
    panelLabel: 'Illustrasjon av et live-nettsted og en staging-kopi',
    items: [
      { title: 'Velg pakke', body: 'Velg størrelsen som passer antall nettsteder og besøk.' },
      { title: 'Vi setter opp eller flytter nettstedet', body: 'Vi installerer WordPress, eller flytter nettstedet du har fra før.' },
      { title: 'Pek domenet', body: 'Du peker domenet mot oss, og vi hjelper deg ved behov.' },
      { title: 'Publiser', body: 'Nettstedet er live. Vi tar oppdateringene og sikkerhetskopiene.' },
    ],
  },
  useCases: {
    eyebrow: 'Bruksområder',
    heading: 'Slik brukes det',
    items: [
      {
        icon: 'domain',
        title: 'Bedriftsnettside',
        body: 'Et raskt og trygt nettsted som du oppdaterer selv, uten å tenke på driften.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'database',
        title: 'Nettbutikk',
        body: 'Kjør en nettbutikk med WooCommerce på samme plattform som resten av nettstedet.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'crm',
        title: 'Byrå med flere kundesider',
        body: 'Samle kundenes nettsteder på ett sted, med felles backup og oppdateringer.',
        link: { label: 'Kontakt oss', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Relatert',
    heading: 'Relaterte tjenester',
    items: [
      { icon: 'server', name: 'inHost — webhotell', href: '/webhotell' },
      { icon: 'virtual', name: 'inVPS — virtuelle servere', href: '/vps' },
      { icon: 'nodejs', name: 'inNode — Node.js-hosting', href: '/nodejs' },
      { icon: 'globe', name: 'inDomain — domener', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      {
        question: 'Hva er forskjellen på inWordPress og inHost?',
        answer:
          'inWordPress er hosting som er satt opp for WordPress, med automatiske oppdateringer, backup og staging. inHost er generelt webhotell for nettsider, e-post og databaser.',
      },
      { question: 'Kan dere flytte nettstedet mitt?', answer: 'Ja. Vi hjelper deg med å flytte et eksisterende WordPress-nettsted fra din nåværende leverandør. Ta kontakt for detaljer.' },
      { question: 'Støtter dere WooCommerce?', answer: 'Ja, du kan kjøre en nettbutikk med WooCommerce. Ta kontakt for anbefalt pakke.' },
      { question: 'Hvilke PHP-versjoner støttes?', answer: 'Vi støtter flere PHP-versjoner og holder dem oppdatert. Ta kontakt for oversikt.' },
      { question: 'Hvordan bestiller jeg?', answer: 'Send oss en e-post med pakken du ønsker, så tar vi kontakt for å sette opp nettstedet ditt.' },
      {
        question: 'Er inSuite tilknyttet WordPress?',
        answer: 'Nei. WordPress er et varemerke tilhørende WordPress Foundation. inSuite er ikke tilknyttet eller godkjent av WordPress Foundation.',
      },
    ],
  },
  cta: {
    heading: 'Klar til å komme i gang?',
    body: 'Kontakt oss, så setter vi opp nettstedet ditt.',
    primary: { label: 'Kontakt oss', href: CONTACT },
  },
  footer: {
    tagline: 'inWordPress — WordPress-hosting som er satt opp og vedlikeholdt for deg.',
    legal: 'inSuite. WordPress er et varemerke tilhørende WordPress Foundation; inSuite er ikke tilknyttet.',
    platformLabel: 'inSuite',
    sectionsLabel: 'inWordPress',
  },
};

const en: WpContent = {
  seoTitle: 'inWordPress — WordPress hosting from inSuite',
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }],
    current: 'inWordPress',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'funksjoner', label: 'Features' },
      { id: 'pakker', label: 'Plans' },
      { id: 'sammenlign', label: 'Compare' },
      { id: 'kom-i-gang', label: 'Get started' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See the plans', href: '#pakker' },
  },
  hero: {
    heading: 'inWordPress',
    subheading:
      'WordPress hosting that is set up, updated and backed up for you, in three sizes you can move between as your sites grow.',
    primaryCta: { label: 'See plans and pricing', href: '#pakker' },
    secondaryCta: { label: 'What you get', href: '#funksjoner' },
    illustrationLabel: 'Illustration of a website, a shield and a speedometer',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'WordPress that just works.',
    body: [
      'inWordPress is hosting set up for WordPress. We take care of updates, backups and security, so you can spend your time on the content instead of the operations.',
      'Do you only need room for websites, email and databases without WordPress? Then inHost is a better fit.',
    ],
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'What it takes to run WordPress safely and fast.',
    panelLabel: 'Illustration of a live website and a staging copy',
    items: [
      { title: 'One-click installation', body: 'Start with a ready-made WordPress site, or move in the one you already have.' },
      { title: 'Automatic updates', body: 'WordPress, plugins and PHP are kept up to date, so you do not have to keep track yourself.' },
      { title: 'Daily backup', body: 'A backup of the site every day, and restore when you need it.' },
      { title: 'Staging environment', body: 'Test changes on a copy of the site before you publish them.' },
      { title: 'Fast performance', body: 'Caching and fast storage, so your pages load quickly.' },
      { title: 'Security', body: 'A firewall and an SSL certificate come with every plan.' },
      { title: 'WP-CLI and SSH', body: 'Manage the site from the command line when you need full control.' },
    ],
  },
  promo: {
    title: 'Not using WordPress?',
    body: 'If you only need room for websites, email and databases, inHost is simpler.',
    cta: { label: 'See inHost', href: '/webhotell' },
  },
  plans: {
    eyebrow: 'Plans',
    heading: 'Plans and pricing',
    intro: 'Pick the plan that fits, and move up a size as your sites grow.',
    cardsHeading: 'Choose a plan',
    fromLabel: 'From',
    footnote: '*Prices are indicative, per month and exclude VAT.',
    items: [
      {
        id: 'start',
        name: 'Start',
        summary: 'For one website.',
        price: '129',
        priceNote: 'NOK / month',
        features: ['1 website', '25,000 visits per month', '10 GB SSD storage', 'Daily backup', 'SSL certificate'],
        cta: { label: 'Order Start', href: CONTACT },
      },
      {
        id: 'vekst',
        name: 'Growth',
        summary: 'For businesses with several websites.',
        price: '299',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in Start, plus',
        features: ['5 websites', '100,000 visits per month', '50 GB SSD storage', 'Staging environment', 'WP-CLI and SSH'],
        cta: { label: 'Order Growth', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Recommended',
      },
      {
        id: 'byra',
        name: 'Agency',
        summary: 'For agencies and demanding websites.',
        price: '699',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in Growth, plus',
        features: ['20 websites', '500,000 visits per month', '200 GB SSD storage', 'Priority support'],
        cta: { label: 'Order Agency', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Compare',
    heading: 'Compare the plans',
    intro: 'See what each plan includes.',
    featureLabel: 'Feature',
    yes: 'Included',
    no: 'Not included',
    rows: [
      { label: 'Capacity', values: [], group: true },
      { label: 'Websites', values: ['1', '5', '20'] },
      { label: 'Visits per month', values: ['25,000', '100,000', '500,000'] },
      { label: 'SSD storage', values: ['10 GB', '50 GB', '200 GB'] },
      { label: 'Features and support', values: [], group: true },
      { label: 'Daily backup', values: [true, true, true] },
      { label: 'SSL certificate', values: [true, true, true] },
      { label: 'Staging environment', values: [false, true, true] },
      { label: 'WP-CLI and SSH', values: [false, true, true] },
      { label: 'Priority support', values: [false, false, true] },
    ],
  },
  steps: {
    eyebrow: 'Get started',
    heading: 'From order to published site',
    intro: 'Four steps, and you do not have to set anything up yourself.',
    panelLabel: 'Illustration of a live website and a staging copy',
    items: [
      { title: 'Choose a plan', body: 'Pick the size that fits your number of websites and visits.' },
      { title: 'We set up or move your site', body: 'We install WordPress, or move the site you already have.' },
      { title: 'Point your domain', body: 'You point your domain to us, and we help if you need it.' },
      { title: 'Publish', body: 'Your site is live. We handle updates and backups.' },
    ],
  },
  useCases: {
    eyebrow: 'Use cases',
    heading: 'How it is used',
    items: [
      {
        icon: 'domain',
        title: 'A business website',
        body: 'A fast and safe website you update yourself, without thinking about the operations.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'database',
        title: 'An online store',
        body: 'Run an online store with WooCommerce on the same platform as the rest of your website.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'crm',
        title: 'An agency with several client sites',
        body: 'Keep your clients’ websites in one place, with shared backup and updates.',
        link: { label: 'Contact us', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Related',
    heading: 'Related services',
    items: [
      { icon: 'server', name: 'inHost — web hosting', href: '/webhotell' },
      { icon: 'virtual', name: 'inVPS — virtual servers', href: '/vps' },
      { icon: 'nodejs', name: 'inNode — Node.js hosting', href: '/nodejs' },
      { icon: 'globe', name: 'inDomain — domains', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'What is the difference between inWordPress and inHost?',
        answer:
          'inWordPress is hosting set up for WordPress, with automatic updates, backup and staging. inHost is general web hosting for websites, email and databases.',
      },
      { question: 'Can you move my website?', answer: 'Yes. We help you move an existing WordPress site from your current provider. Get in touch for details.' },
      { question: 'Do you support WooCommerce?', answer: 'Yes, you can run an online store with WooCommerce. Get in touch for a recommended plan.' },
      { question: 'Which PHP versions are supported?', answer: 'We support several PHP versions and keep them up to date. Get in touch for the list.' },
      { question: 'How do I order?', answer: 'Send us an email with the plan you want, and we will get in touch to set up your website.' },
      {
        question: 'Is inSuite affiliated with WordPress?',
        answer: 'No. WordPress is a trademark of the WordPress Foundation. inSuite is not affiliated with or endorsed by the WordPress Foundation.',
      },
    ],
  },
  cta: {
    heading: 'Ready to get started?',
    body: 'Contact us and we will set up your website.',
    primary: { label: 'Contact us', href: CONTACT },
  },
  footer: {
    tagline: 'inWordPress — WordPress hosting set up and maintained for you.',
    legal: 'inSuite. WordPress is a trademark of the WordPress Foundation; inSuite is not affiliated.',
    platformLabel: 'inSuite',
    sectionsLabel: 'inWordPress',
  },
};

export const WP_CONTENT: Record<Locale, WpContent> = { no, en };
